import { CalendarOutlined, ClockCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Select, TimePicker } from "antd";
import dayjs from "dayjs";
import { DailyAttendanceTable } from "@/modules/daily-attendance/components/DailyAttendanceTable";
import { useCreateDailyAttendance } from "@/modules/daily-attendance/hooks/useDailyAttendance";
import type {
	AttendancePeriod,
	CreateDailyAttendanceRequest,
} from "@/modules/daily-attendance/types";
import { useStaffList } from "@/modules/staff/hooks/useStaff";
import type { UrlParamPatch } from "@/shared/hooks/useUrlState";
import { useUrlTableState } from "@/shared/hooks/useUrlState";

interface FormValues {
	staffId: string;
	date: dayjs.Dayjs;
	period: AttendancePeriod;
	time: dayjs.Dayjs;
	overrideReason: string;
}

interface OrgAttendanceTabProps {
	organizationId: string;
	organizationName?: string;
}

export function OrgAttendanceTab({ organizationId, organizationName }: OrgAttendanceTabProps) {
	const [form] = Form.useForm<FormValues>();
	const { page, limit, setPage, getFilter, setFilters } = useUrlTableState({ prefix: "att" });
	// Default filter date (today) is kept out of the URL.
	const filterDate = getFilter("date") ?? dayjs().format("YYYY-MM-DD");
	const filterStaffId = getFilter("staffId") ?? null;

	const { mutate: create, isPending } = useCreateDailyAttendance();

	const { data: staffData, isLoading: staffLoading } = useStaffList({
		organizationId,
		page: 1,
		limit: 100,
		isActive: true,
	});

	const staffOptions =
		staffData?.data?.items?.map((s) => ({ value: s.id, label: s.fullname })) ?? [];

	const handleFinish = (values: FormValues) => {
		const baseDate = values.date;
		const payload: CreateDailyAttendanceRequest = {
			date: baseDate.format("YYYY-MM-DD"),
			staffId: values.staffId,
			organizationId,
			period: values.period,
			time: baseDate
				.hour(values.time.hour())
				.minute(values.time.minute())
				.second(0)
				.millisecond(0)
				.toISOString(),
			overrideReason: values.overrideReason,
		};

		create(payload, {
			onSuccess: () => {
				const submittedDate = baseDate.format("YYYY-MM-DD");
				setFilters({ date: submittedDate, staffId: values.staffId });
				form.resetFields(["staffId", "period", "time", "overrideReason"]);
			},
		});
	};

	return (
		<div>
			<Form
				form={form}
				layout="inline"
				onFinish={handleFinish}
				initialValues={{ date: dayjs(), period: "MORNING" as AttendancePeriod }}
				className="mb-4 flex flex-wrap gap-y-2"
				onValuesChange={(changed) => {
					const patch: UrlParamPatch = {};
					if (changed.date) {
						patch.date = dayjs(changed.date).format("YYYY-MM-DD");
					}
					if ("staffId" in changed) {
						patch.staffId = changed.staffId ?? null;
					}
					if (Object.keys(patch).length > 0) {
						setFilters(patch);
					}
				}}
			>
				<Form.Item
					name="staffId"
					label="* Xodim"
					rules={[{ required: true, message: "Xodimni tanlang" }]}
					className="mb-0"
				>
					<Select
						placeholder="Xodimni tanlang"
						allowClear
						showSearch
						optionFilterProp="label"
						loading={staffLoading}
						options={staffOptions}
						style={{ width: 220 }}
						notFoundContent={staffLoading ? "Yuklanmoqda..." : "Xodim topilmadi"}
					/>
				</Form.Item>

				<Form.Item
					name="date"
					label="* Sana"
					rules={[{ required: true, message: "Sanani tanlang" }]}
					className="mb-0"
				>
					<DatePicker
						format="DD.MM.YYYY"
						suffixIcon={<CalendarOutlined />}
						style={{ width: 150 }}
					/>
				</Form.Item>

				<Form.Item
					name="period"
					label="* Davr"
					rules={[{ required: true, message: "Davrni tanlang" }]}
					className="mb-0"
				>
					<Select
						style={{ width: 130 }}
						options={[
							{ value: "MORNING", label: "Ertalab" },
							{ value: "NOON", label: "Kunduz" },
							{ value: "EVENING", label: "Kechqurun" },
						]}
					/>
				</Form.Item>

				<Form.Item
					name="time"
					label="* Vaqt"
					rules={[{ required: true, message: "Vaqtni tanlang" }]}
					className="mb-0"
				>
					<TimePicker
						placeholder="HH:mm"
						format="HH:mm"
						suffixIcon={<ClockCircleOutlined />}
						minuteStep={5}
						style={{ width: 120 }}
					/>
				</Form.Item>

				<Form.Item
					name="overrideReason"
					label="* Sabab"
					rules={[{ required: true, message: "Sabab ko'rsatilishi shart" }]}
					className="mb-0"
				>
					<Input placeholder="Sababni kiriting..." maxLength={500} style={{ width: 200 }} />
				</Form.Item>

				<Form.Item className="mb-0">
					<Button type="primary" htmlType="submit" loading={isPending} icon={<PlusOutlined />}>
						Qo&apos;shish
					</Button>
				</Form.Item>
			</Form>

			<DailyAttendanceTable
				organizationId={organizationId}
				organizationName={organizationName}
				date={filterDate}
				status={null}
				staffId={filterStaffId}
				page={page}
				limit={limit}
				// antd's pagination onChange forwards pageSize as the second
				// argument, so setPage updates page and limit in one entry.
				onPageChange={setPage}
			/>
		</div>
	);
}
